import { useState } from 'react';
import { ChevronDown, Mail } from 'lucide-react';
import ContactImage from '../assets/images/contact_img.png';
import Navbar from '../components/ArtisttoHeader';
import ArtisttoFooter from '../components/ArtisttoFooter';
import { GOOGLE_SCRIPT_URL } from '../config/googleSheets';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    artistName: '',
    phoneNumber: '',
    email: '',
    message: '',
    category: 'Select Category',
    songLink: ''
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const categories = ['Audio Production','Video Production','Music Marketing','Community','Other'];

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.artistName || !formData.phoneNumber || !formData.email || !formData.message || formData.category === 'Select Category') {
      setSubmitStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      if (GOOGLE_SCRIPT_URL === 'YOUR_GOOGLE_APPS_SCRIPT_URL_HERE') {
        throw new Error('Google Apps Script URL not configured. Please update the config file.');
      }
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        mode: 'no-cors'
      });
      setSubmitStatus({ type: 'success', message: "Your message has been sent successfully! We'll get back to you soon." });
      setFormData({ artistName: '', phoneNumber: '', email: '', message: '', category: 'Select Category', songLink: '' });
    } catch (err) {
      console.error('Error submitting form:', err);
      setSubmitStatus({ type: 'error', message: 'There was an error sending your message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Inter font */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <Navbar />

      {/* ENFORCE INTER EVERYWHERE INSIDE THIS PAGE */}
      <div className="min-h-screen bg-black text-white md:pt-[150px] pt-40 pb-14 font-inter [&_*]:font-inter">
        <div className="max-w-[1300px] mx-auto px-4 md:px-6">

          {/* MOBILE HEADER */}
          <div className="lg:hidden mb-8">
            <div className="flex items-center gap-2 text-[#FF8000] mb-2">
              <span className="text-[#FF8000] text-sm">✦</span>
              <span className="tracking-[0.24em] text-xs">CONTACT US</span>
            </div>
            <h1 className="text-[28px] font-semibold leading-[1.1]">
              GET IN TOUCH WITH US
            </h1>
            <p className="text-[#989898] text-[14px] mt-4">
              Connect with our support teams for any queries or questions.
            </p>
            <div className="h-px bg-white/10 mt-6" />
          </div>

          {/* DESKTOP ROW */}
          <div className="lg:flex lg:items-start">
            {/* LEFT PANEL */}
            <aside className="hidden lg:block lg:w-[470px]">
              <div className="mb-8 pl-1">
                <div className="flex items-center gap-2 text-[#FF8000] mb-3">
                  <span className="text-[#FF8000] text-base leading-none">✦</span>
                  <span className="tracking-[0.24em] text-sm">CONTACT US</span>
                </div>
                <h2 className="text-[48px] font-semibold leading-[1.1]">
                  GET IN TOUCH<br />WITH US
                </h2>
                <p className="text-[#989898] text-[14px] mt-5">
                  Connect with our support teams for any queries or questions.
                </p>
                <div className="h-px bg-white/10 mt-6" />
              </div>

              {/* Emails */}
              <div className="space-y-6">
                {/* <a href="mailto:connect.artistto@gmail.com" className="flex items-center gap-4">
                  <Mail className="w-6 h-6 text-white" />
                  <span className="text-white text-[16px]">connect.artistto@gmail.com</span>
                </a> */}
                <a href="mailto:connect@artistto.com" className="flex items-center gap-4">
                  <Mail className="w-6 h-6 text-white" />
                  <span className="text-white text-[16px]">connect@artistto.com</span>
                </a>
              </div>

              {/* Image (fixed 415 × 392) */}
              <div className="mt-8">
                <img
                  src={ContactImage}
                  alt="Studio gear"
                  style={{ width: '415px', height: '392px' }}
                  className="rounded-xl object-cover border border-white/10"
                />
              </div>
            </aside>

            {/* VERTICAL DIVIDER */}
            <div className="hidden lg:block w-px bg-white/10 mx-8" />

            {/* FORM PANEL */}
            <section className="flex-1">
              <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-black p-4 md:p-6 lg:p-[41px]">
                <div className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-[15px] mb-2">
                      Name<span className="text-white">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.artistName}
                      onChange={(e) => handleInputChange('artistName', e.target.value)}
                      placeholder="Your name"
                      className="block w-full h-12 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[#2A2A2A] px-4 text-white text-[14px] placeholder:text-white/50 placeholder:text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF8000]/60 focus:border-transparent"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-[15px] mb-2">
                      Email<span className="text-white">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Email"
                      className="block w-full h-12 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[#2A2A2A] px-4 text-white text-[14px] placeholder:text-white/50 placeholder:text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF8000]/60 focus:border-transparent"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-[15px] mb-2">
                      Phone Number<span className="text-white">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      placeholder="Phone Number"
                      className="block w-full h-12 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[#2A2A2A] px-4 text-white text-[14px] placeholder:text-white/50 placeholder:text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF8000]/60 focus:border-transparent"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-[15px] mb-2">
                      What Service are you interested in?<span className="text-white">*</span>
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsDropdownOpen(v => !v)}
                        className="block w-full h-12 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[#2A2A2A] px-4 text-left text-white text-[14px] placeholder:text-white/50 placeholder:text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF8000]/60 focus:border-transparent flex items-center justify-between"
                      >
                        <span className={formData.category === 'Select Category' ? 'text-white/50' : 'text-white'}>
                          {formData.category}
                        </span>
                        <ChevronDown className={`w-5 h-5 text-white/60 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full rounded-lg border border-[#2A2A2A] bg-black shadow-lg overflow-hidden">
                          {categories.map((category) => (
                            <button
                              key={category}
                              type="button"
                              onClick={() => { handleInputChange('category', category); setIsDropdownOpen(false); }}
                              className="w-full text-left px-4 py-3 text-white text-[14px] hover:bg-[#0F0F0F]"
                            >
                              {category}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Song Link */}
                  <div>
                    <label className="block text-[15px] mb-2">Song Link (Optional)</label>
                    <input
                      type="url"
                      value={formData.songLink}
                      onChange={(e) => handleInputChange('songLink', e.target.value)}
                      placeholder="Song Link"
                      className="block w-full h-12 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[#2A2A2A] px-4 text-white text-[14px] placeholder:text-white/50 placeholder:text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF8000]/60 focus:border-transparent"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-[15px] mb-2">
                      Message<span className="text-white">*</span>
                    </label>
                    <textarea
                      rows={6}
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Type your message here"
                      className="block w-full rounded-lg bg-[rgba(255,255,255,0.04)] border border-[#2A2A2A] px-4 py-3 text-white text-[14px] placeholder:text-white/50 placeholder:text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FF8000]/60 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Status */}
                  {submitStatus.message && (
                    <div
                      className={`p-3 rounded-lg border ${
                        submitStatus.type === 'success'
                          ? 'bg-green-900/40 border-green-600 text-green-100'
                          : 'bg-red-900/40 border-red-600 text-red-100'
                      }`}
                    >
                      {submitStatus.message}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-6 h-12 text-sm font-medium transition-colors
                      ${isSubmitting ? 'bg-gray-600 cursor-not-allowed' : 'bg-[#FF8000] hover:bg-[#e67300]'}`}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      </div>

      <ArtisttoFooter />
    </>
  );
}
